import React, { useState } from "react";
import { Create, useForm } from "@refinedev/antd";
import { Form, Input, Select, Checkbox, Button, Space, Card, Table, Popconfirm } from "antd";
import { PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import { CREATE_DEFAULT_COMMITTEE_MUTATION } from "./queries";
import { IDefaultCommittee, IDefaultCommitteeMember } from "./interface";

const { Option } = Select;

export const DefaultCommitteeCreate: React.FC = () => {
  const { formProps, saveButtonProps } = useForm<IDefaultCommittee>({
    resource: "defaultCommittees",
    meta: {
      gqlMutation: CREATE_DEFAULT_COMMITTEE_MUTATION,
    },
  });

  const [members, setMembers] = useState<IDefaultCommitteeMember[]>([]);
  const [newMember, setNewMember] = useState<IDefaultCommitteeMember>({
    fullName: "",
    role: "MEMBER",
    organization: "",
    linkedUserId: undefined,
    isActive: true,
  });

  const handleAddMember = () => {
    if (!newMember.fullName.trim()) {
      return;
    }

    setMembers([...members, { ...newMember }]);
    setNewMember({
      fullName: "",
      role: "MEMBER",
      organization: "",
      linkedUserId: undefined,
      isActive: true,
    });
  };

  const handleRemoveMember = (index: number) => {
    const newMembers = [...members];
    newMembers.splice(index, 1);
    setMembers(newMembers);
  };

  const handleMemberChange = (field: keyof IDefaultCommitteeMember, value: any) => {
    setNewMember({ ...newMember, [field]: value });
  };

  const handleFinish = async (values: any) => {
    if (formProps.onFinish) {
      formProps.onFinish({
        ...values,
        members,
      });
    }
  };

  const memberColumns = [
    {
      title: "Full Name",
      dataIndex: "fullName",
      key: "fullName",
    },
    {
      title: "Role",
      dataIndex: "role",
      key: "role",
      render: (role: string) => role.toLowerCase().replace("_", " "),
    },
    {
      title: "Organization",
      dataIndex: "organization",
      key: "organization",
    },
    {
      title: "Status",
      dataIndex: "isActive",
      key: "isActive",
      render: (isActive: boolean) => (
        <span style={{ color: isActive ? "#52c41a" : "#ff4d4f" }}>
          {isActive ? "Active" : "Inactive"}
        </span>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_: any, __: any, index: number) => (
        <Popconfirm
          title="Are you sure you want to delete this member?"
          onConfirm={() => handleRemoveMember(index)}
          okText="Yes"
          cancelText="No"
        >
          <Button type="text" danger icon={<DeleteOutlined />} size="small">
            Delete
          </Button>
        </Popconfirm>
      ),
    },
  ];

  return (
    <Create saveButtonProps={saveButtonProps} title="Create Default Committee">
      <Form layout="vertical" {...formProps} onFinish={handleFinish}>
        <Form.Item
          label="Committee Name"
          name="name"
          rules={[{ required: true, message: "Please enter committee name" }]}
        >
          <Input placeholder="Enter committee name" />
        </Form.Item>

        <Form.Item label="Applicable Minute Types" name="applicableMinuteTypes">
          <Select
            mode="multiple"
            placeholder="Select applicable minute types"
            style={{ width: "100%" }}
          >
            <Option value="BID_OPENING">Bid Opening</Option>
            <Option value="EVALUATION_AWARD">Evaluation and Award</Option>
          </Select>
        </Form.Item>

        <Form.Item label="Active" name="isActive" valuePropName="checked">
          <Checkbox>Active</Checkbox>
        </Form.Item>

        <Card title="Committee Members" style={{ marginTop: 16 }}>
          <Space direction="vertical" style={{ width: "100%", marginBottom: 16 }}>
            <Space.Compact style={{ width: "100%" }}>
              <Input
                placeholder="Full Name"
                value={newMember.fullName}
                onChange={(e) => handleMemberChange("fullName", e.target.value)}
                style={{ width: "25%" }}
              />
              <Select
                value={newMember.role}
                onChange={(value) => handleMemberChange("role", value)}
                style={{ width: "20%" }}
              >
                <Option value="CHAIR">Chair</Option>
                <Option value="MEMBER">Member</Option>
                <Option value="SECRETARY">Secretary</Option>
                <Option value="OBSERVER">Observer</Option>
              </Select>
              <Input
                placeholder="Organization"
                value={newMember.organization}
                onChange={(e) => handleMemberChange("organization", e.target.value)}
                style={{ width: "25%" }}
              />
              <Select
                value={newMember.isActive}
                onChange={(value) => handleMemberChange("isActive", value)}
                style={{ width: "15%" }}
              >
                <Option value={true}>Active</Option>
                <Option value={false}>Inactive</Option>
              </Select>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleAddMember}
                style={{ width: "15%" }}
              >
                Add Member
              </Button>
            </Space.Compact>
          </Space>

          <Table
            dataSource={members}
            columns={memberColumns}
            rowKey={(record, index) => (index ?? Date.now() + Math.random()).toString()}
            bordered
            pagination={false}
            style={{ marginTop: 16 }}
          />
        </Card>
      </Form>
    </Create>
  );
};
